import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Paper,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Domain as DomainIcon,
  Category as CategoryIcon,
  Group as GroupIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import {
  Organization,
  OrganizationMember,
  OrganizationRole,
  getRoleLabel,
  getRoleColor,
  isRoleEqual,
  isRoleGreaterOrEqual,
} from '../../services/organizationApi';

interface OrganizationOverviewProps {
  organization: Organization;
  members: OrganizationMember[];
  onEdit: () => void;
  onDelete: () => void;
  canEdit: boolean;
  canDelete: boolean;
}

const OrganizationOverview: React.FC<OrganizationOverviewProps> = ({
  organization,
  members,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
}) => {
  const owner = members.find(member => isRoleEqual(member.role, OrganizationRole.OWNER));
  const admins = members.filter(member => isRoleEqual(member.role, OrganizationRole.ADMIN));
  const regularMembers = members.filter(member => 
    isRoleGreaterOrEqual(member.role, OrganizationRole.MEMBER)
  );

  return (
    <Box>
      {/* Header with Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            {organization.name}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {organization.description}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canEdit && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={onEdit}
            >
              Edit
            </Button>
          )}
          {canDelete && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={onDelete}
            >
              Delete
            </Button>
          )}
        </Box>
      </Box>

      {/* Organization Details */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
        gap: 3,
        mb: 4 
      }}>
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <BusinessIcon sx={{ mr: 1 }} />
                Organization Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Domain
                </Typography>
                <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                  <DomainIcon sx={{ mr: 1, fontSize: 16 }} />
                  {organization.domain}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Industry
                </Typography>
                <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                  <CategoryIcon sx={{ mr: 1, fontSize: 16 }} />
                  {organization.industry}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Company Size
                </Typography>
                <Typography variant="body1">
                  {organization.size}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                  <CalendarIcon sx={{ mr: 1, fontSize: 16 }} />
                  {new Date(organization.created_at).toLocaleDateString()}
                </Typography>
              </Box>

              {organization.updated_at !== organization.created_at && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarIcon sx={{ mr: 1, fontSize: 16 }} />
                    {new Date(organization.updated_at).toLocaleDateString()}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <GroupIcon sx={{ mr: 1 }} />
                Member Summary
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Members
                </Typography>
                <Typography variant="h4" color="primary">
                  {members.length}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Role Distribution
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {owner && (
                    <Chip
                      label={`Owner: 1`}
                      color="error"
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {admins.length > 0 && (
                    <Chip
                      label={`Admins: ${admins.length}`}
                      color="primary"
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {regularMembers.length > 0 && (
                    <Chip
                      label={`Members: ${regularMembers.length}`}
                      color="success"
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Organization Owner
                </Typography>
                {owner && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Chip
                      label={getRoleLabel(owner.role)}
                      sx={{ 
                        backgroundColor: getRoleColor(owner.role),
                        color: 'white',
                        mr: 1
                      }}
                      size="small"
                    />
                    <Typography variant="body2">
                      {owner.user.username} ({owner.user.email})
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Quick Stats */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Quick Statistics
        </Typography>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 3 
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="primary">
              {members.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Members
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="success.main">
              {admins.length + 1} {/* +1 for owner */}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Administrators
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="info.main">
              {regularMembers.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Regular Members
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="warning.main">
              {new Date(organization.created_at).getFullYear()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Founded
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Permission Notice */}
      {!canEdit && !canDelete && (
        <Paper sx={{ p: 2, backgroundColor: 'info.light', color: 'info.contrastText' }}>
          <Typography variant="body2">
            <strong>Note:</strong> You have view-only access to this organization. 
            Only organization owners can make changes to organization details, manage members, or delete the organization.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default OrganizationOverview;
